import { Modal, notification } from "antd";
import React, { FC } from "react";
import { useFormikContext } from "formik";
import Title from "antd/es/typography/Title";
import { useTranslation } from "react-i18next";
import {
  FormSelect,
  FormInput,
  GlobalDatePicker,
  DatePickerValue,
} from "shared/ui";
import { Colors } from "shared/themes";
import { useCreateOrderMutation } from "shared/api";
import {
  dayjs,
  handleRequestError,
  handleValidOrderDate,
} from "shared/helpers";
import { useValidOrderDate } from "shared/hooks";
import { AdaptedValues, AddOrUpdateOrderValues } from "shared/types";
import { ModalSizeValue, getStatusDropdownValues } from "shared/constants";

import { DynamicCompanyFields } from "./DynamicCompanyFields";
import { SupplierCompanyItem } from "../../../../shared/api/suppliers/models";

interface AddOrderModalProps {
  isOpenModal: boolean;
  hideAddOrderModal: () => void;
  companiesAdapted: AdaptedValues[];
  adaptedProductCard: AdaptedValues[];
  dynamicCompanyFields?: SupplierCompanyItem;
  adaptedSubBuyers: AdaptedValues[];
}

const AddOrderModal: FC<AddOrderModalProps> = ({
  isOpenModal,
  companiesAdapted,
  adaptedProductCard,
  dynamicCompanyFields,
  hideAddOrderModal,
  adaptedSubBuyers,
}) => {
  const {
    values,
    resetForm,
    handleBlur,
    handleChange,
    isValid,
    setFieldValue,
    errors,
  } = useFormikContext<AddOrUpdateOrderValues>();
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const timezone = localStorage.getItem("timezone") ?? "UTC";

  const closeModal = () => {
    hideAddOrderModal();
    resetForm();
  };

  const handleWantedDateTimeChange = (
    _: DatePickerValue,
    dateString: string
  ) => {
    setFieldValue(
      "wanted_delivery_datetime",
      dayjs.tz(dateString, timezone).format()
    );
  };

  const handleProductionDateTimeChange = (
    _: DatePickerValue,
    dateString: string
  ) => {
    setFieldValue(
      "production_datetime",
      dayjs.tz(dateString, timezone).format()
    );
  };

  const [createOrder] = useCreateOrderMutation();

  const createOrderRequest = async () => {
    try {
      await createOrder({
        buyer: values.subBuyer ? values.subBuyer : values.buyer!,
        po_number: values.po_number!,
        product_card: values.product_card!,
        production_datetime: dayjs(values.production_datetime).utc().format(),
        quantity_expected: values.quantity_expected!,
        status: values.status!,
        wanted_delivery_datetime: dayjs(values.wanted_delivery_datetime)
          .utc()
          .format(),
        dynamic_order_fields: values.dynamic_order_fields,
        customer_order_number: values.customer_order_number!,
      }).unwrap();
      api.success({
        message: t("orders.success"),
        description: t("orders.add-msg"),
      });
      hideAddOrderModal();
      resetForm();
    } catch (error) {
      const description = handleRequestError(error, t("orders.error"));
      api.error({
        message: t("orders.error"),
        description,
      });
    }
  };

  const isValidDate = useValidOrderDate(
    isOpenModal,
    values,
    handleValidOrderDate,
    api
  );

  return (
    <>
      {contextHolder}
      <Modal
        title={t("orders.add-order")}
        open={isOpenModal}
        width={ModalSizeValue.X_MEDIUM}
        okButtonProps={{
          disabled: !isValid || !isValidDate,
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
        onCancel={closeModal}
        onOk={() => {
          createOrderRequest();
        }}
      >
        <Title level={5}>{t("orders.buyer")}</Title>

        <FormSelect
          value={values.buyer}
          placeholder={t("orders.buyer-placeholder")}
          fieldName="buyer"
          options={companiesAdapted}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.buyer?.toString()}
          isError={!!errors.buyer}
          showSearch
        />

        {values?.buyer && (
          <>
            <Title level={5}>{t("orders.subBuyer")}</Title>

            <FormSelect
              value={values?.subBuyer}
              placeholder={t("orders.subBuyer-placeholder")}
              fieldName="subBuyer"
              options={adaptedSubBuyers}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
              showSearch
            />
          </>
        )}

        <Title level={5} className="mb-1.25">
          {t("orders.po-number")}
        </Title>

        <FormInput
          value={values.po_number}
          fieldName="po_number"
          placeholder={t("orders.po-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.po_number}
          isError={!!errors.po_number}
        />

        <Title level={5} className="mb-1.25">
          {t("orders.customer-order-number")}
        </Title>

        <FormInput
          value={values.customer_order_number!}
          fieldName="customer_order_number"
          placeholder={t("orders.customer-order-number-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.customer_order_number}
          isError={!!errors.customer_order_number}
        />

        <Title level={5} className="mb-2 mt-2">
          {t("orders.production-datetime")}
        </Title>

        <GlobalDatePicker
          handlePickerChange={handleProductionDateTimeChange}
          error={errors.production_datetime}
          isError={!!errors.production_datetime}
          showTime
        />

        <Title level={5} className="mb-1.25">
          {t("orders.wanter-delivery-datetime")}
        </Title>

        <GlobalDatePicker
          handlePickerChange={handleWantedDateTimeChange}
          error={errors.wanted_delivery_datetime}
          isError={!!errors.wanted_delivery_datetime}
          minDate={dayjs.utc(values.production_datetime)}
          disabled={!values.production_datetime}
          showTime
        />

        <Title level={5} className="mt-1.25">
          {t("orders.product-card")}
        </Title>

        <FormSelect
          value={values.product_card}
          placeholder={t("orders.product-card-placeholder")}
          fieldName="product_card"
          options={adaptedProductCard}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.product_card?.toString()}
          isError={!!errors.product_card}
          showSearch
        />

        <Title level={5} className="mb-1.25">
          {t("orders.quantity-expected")}
        </Title>

        <FormInput
          value={values.quantity_expected}
          fieldName="quantity_expected"
          placeholder={t("orders.quantity-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.quantity_expected}
          isError={!!errors.quantity_expected}
          numberType
        />

        <Title level={5}>{t("orders.status")}</Title>

        <FormSelect
          value={values.status}
          placeholder={t("orders.status-placeholder")}
          fieldName="status"
          options={getStatusDropdownValues(t)}
          setFieldValue={setFieldValue}
          handleBlur={handleBlur}
          error={errors.status?.toString()}
          isError={!!errors.status}
          showSearch
        />

        <DynamicCompanyFields dynamicCompanyFields={dynamicCompanyFields} />
      </Modal>
    </>
  );
};

export { AddOrderModal };
